'use client'

import { useEffect, useRef, useState } from 'react'
import { Activity, Loader2, LogIn, RefreshCw, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'

type ForumActivitySession = {
  userId: number
  email: string
  handle: string | null
  callsign: string | null
  hamCallsign: string | null
  gmrsCallsign: string | null
  status: string
  lastSeenAt: string
  ipHash: string
  userAgent: string
}

type ForumLoginEvent = {
  id: number
  userId: number
  email: string
  handle: string | null
  callsign: string | null
  hamCallsign: string | null
  gmrsCallsign: string | null
  status: string
  loggedInAt: string
  ipHash: string
  userAgent: string
}

function formatDate(value: string) {
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function AdminForumLogins() {
  const [authorized, setAuthorized] = useState<boolean | null>(null)
  const [activity, setActivity] = useState<ForumActivitySession[]>([])
  const [events, setEvents] = useState<ForumLoginEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const requestId = useRef(0)

  async function loadEvents(search = query) {
    const currentRequest = ++requestId.current
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ limit: '200' })
      const normalizedSearch = search.trim()
      if (normalizedSearch) params.set('q', normalizedSearch)
      const response = await fetch(`/api/admin/forum/logins?${params}`)
      if (!response.ok) {
        setAuthorized(false)
        return
      }
      const data = (await response.json()) as {
        activity?: ForumActivitySession[]
        events?: ForumLoginEvent[]
      }
      if (currentRequest !== requestId.current) return
      setAuthorized(true)
      setActivity(data.activity || [])
      setEvents(data.events || [])
    } catch {
      if (currentRequest !== requestId.current) return
      setAuthorized(false)
      setError('Could not load forum login activity.')
    } finally {
      if (currentRequest === requestId.current) setLoading(false)
    }
  }

  useEffect(() => {
    const timeout = window.setTimeout(() => loadEvents(query), 250)
    return () => window.clearTimeout(timeout)
  }, [query])

  return (
    <div className="mx-auto w-full max-w-5xl">
      <section className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
              <LogIn className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-semibold">Forum access activity</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Recently active signed-in sessions and successful password logins. IP addresses
                are stored only as one-way hashes.
              </p>
            </div>
          </div>
          {authorized && (
            <Button
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={() => loadEvents()}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
          )}
        </div>

        {authorized && (
          <div className="relative mt-5">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search activity, handle, HAM/GMRS callsign, or legacy callsign"
              aria-label="Search forum access activity"
              className="w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-10 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear forum activity search"
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : authorized === false ? (
          <p className="mt-6 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Administrator access is required.
          </p>
        ) : (
          <div
            role="region"
            aria-label="Forum access activity results"
            tabIndex={0}
            className="mt-6 max-h-[65vh] space-y-8 overflow-y-auto overscroll-contain pr-2 [scrollbar-gutter:stable] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
          >
            <section>
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                <h3 className="font-semibold">Recently active sessions</h3>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Last authenticated request per member, updated at most every five minutes.
              </p>
              {activity.length === 0 ? (
                <p className="mt-3 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  {query.trim()
                    ? 'No active sessions match that search.'
                    : 'No authenticated session activity has been recorded yet.'}
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto rounded-xl border border-border">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead className="bg-secondary/70 text-xs text-muted-foreground uppercase">
                      <tr>
                        <th className="px-3 py-2.5 font-medium">Last seen</th>
                        <th className="px-3 py-2.5 font-medium">Member</th>
                        <th className="px-3 py-2.5 font-medium">Status</th>
                        <th className="px-3 py-2.5 font-medium">Client</th>
                        <th className="px-3 py-2.5 font-medium">IP fingerprint</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((session) => (
                        <tr key={session.userId} className="border-t border-border align-top">
                          <td className="whitespace-nowrap px-3 py-3 text-xs text-muted-foreground">
                            {formatDate(session.lastSeenAt)}
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium">{session.handle || session.email}</p>
                            {session.handle && (
                              <p className="mt-0.5 text-xs text-muted-foreground">{session.email}</p>
                            )}
                            {(session.hamCallsign ||
                              session.gmrsCallsign ||
                              session.callsign) && (
                              <p className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                                {session.hamCallsign && <span>HAM {session.hamCallsign}</span>}
                                {session.gmrsCallsign && <span>GMRS {session.gmrsCallsign}</span>}
                                {!session.hamCallsign &&
                                  !session.gmrsCallsign &&
                                  session.callsign && <span>{session.callsign}</span>}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={
                                session.status === 'active'
                                  ? 'rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300'
                                  : 'rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive'
                              }
                            >
                              {session.status}
                            </span>
                          </td>
                          <td className="max-w-72 px-3 py-3">
                            <p
                              className="truncate text-xs text-muted-foreground"
                              title={session.userAgent || undefined}
                            >
                              {session.userAgent || 'Unknown client'}
                            </p>
                          </td>
                          <td
                            className="whitespace-nowrap px-3 py-3 font-mono text-xs text-muted-foreground"
                            title={session.ipHash}
                          >
                            {session.ipHash ? `${session.ipHash.slice(0, 12)}…` : 'Not recorded'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section>
              <div className="flex items-center gap-2">
                <LogIn className="h-4 w-4 text-primary" />
                <h3 className="font-semibold">Password login history</h3>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                New login events only; returning with an existing session appears above.
              </p>
              {events.length === 0 ? (
                <p className="mt-3 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  {query.trim()
                    ? 'No password logins match that search.'
                    : 'No password logins have been recorded yet.'}
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto rounded-xl border border-border">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead className="bg-secondary/70 text-xs text-muted-foreground uppercase">
                      <tr>
                        <th className="px-3 py-2.5 font-medium">Login time</th>
                        <th className="px-3 py-2.5 font-medium">Member</th>
                        <th className="px-3 py-2.5 font-medium">Status</th>
                        <th className="px-3 py-2.5 font-medium">Client</th>
                        <th className="px-3 py-2.5 font-medium">IP fingerprint</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.id} className="border-t border-border align-top">
                          <td className="whitespace-nowrap px-3 py-3 text-xs text-muted-foreground">
                            {formatDate(event.loggedInAt)}
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium">{event.handle || event.email}</p>
                            {event.handle && (
                              <p className="mt-0.5 text-xs text-muted-foreground">{event.email}</p>
                            )}
                            {(event.hamCallsign ||
                              event.gmrsCallsign ||
                              event.callsign) && (
                              <p className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                                {event.hamCallsign && <span>HAM {event.hamCallsign}</span>}
                                {event.gmrsCallsign && <span>GMRS {event.gmrsCallsign}</span>}
                                {!event.hamCallsign &&
                                  !event.gmrsCallsign &&
                                  event.callsign && <span>{event.callsign}</span>}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={
                                event.status === 'active'
                                  ? 'rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300'
                                  : 'rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive'
                              }
                            >
                              {event.status}
                            </span>
                          </td>
                          <td className="max-w-72 px-3 py-3">
                            <p
                              className="truncate text-xs text-muted-foreground"
                              title={event.userAgent || undefined}
                            >
                              {event.userAgent || 'Unknown client'}
                            </p>
                          </td>
                          <td
                            className="whitespace-nowrap px-3 py-3 font-mono text-xs text-muted-foreground"
                            title={event.ipHash}
                          >
                            {event.ipHash.slice(0, 12)}…
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {error}
          </p>
        )}
      </section>
    </div>
  )
}
